import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Pull the base URL from your .env file to avoid 404/Cannot POST errors
const API_URL = process.env.REACT_APP_API_BASE_URL;

/* -------------------------------------------------------------------------- */
/* Async Thunks                                */
/* -------------------------------------------------------------------------- */

/**
 * Triggers the background sync process.
 * Payload: UnifiedSyncDto (sourceType, companyId, source, destination, process)
 */
export const triggerBulkSync = createAsyncThunk(
  "bulkAction/triggerSync",
  async (payload, { rejectWithValue }) => {
    try {
      // Combined the API_URL with the specific endpoint
      const response = await axios.post(`${API_URL}/bulk-action/sync`, payload);
      
      // Expected backend response: { message: "Sync started", syncId: "uuid-..." }
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to start background sync";
      return rejectWithValue(Array.isArray(msg) ? msg.join(", ") : msg);
    }
  }
);

/* -------------------------------------------------------------------------- */
/* Slice                                    */
/* -------------------------------------------------------------------------- */

const initialState = {
  isProcessing: false,
  status: "idle",      // 'idle' | 'loading' | 'succeeded' | 'failed'
  currentSyncId: null, // Track the specific job ID from the backend
  error: null,
  message: "",
};

const bulkActionSlice = createSlice({
  name: "bulkAction",
  initialState,
  reducers: {
    /**
     * Resets the sync state so the UI can be used for a new operation.
     */
    clearSyncState: (state) => {
      state.status = "idle";
      state.currentSyncId = null;
      state.error = null;
      state.message = "";
      state.isProcessing = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Trigger Sync Cases
      .addCase(triggerBulkSync.pending, (state) => {
        state.isProcessing = true;
        state.status = "loading";
        state.error = null;
      })
      .addCase(triggerBulkSync.fulfilled, (state, action) => {
        state.isProcessing = false;
        state.status = "succeeded";
        state.message = action.payload.message;
        state.currentSyncId = action.payload.syncId; // Successfully captured the job ID
        state.error = null;
      })
      .addCase(triggerBulkSync.rejected, (state, action) => {
        state.isProcessing = false;
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

/* -------------------------------------------------------------------------- */
/* Selectors                                 */
/* -------------------------------------------------------------------------- */

export const { clearSyncState } = bulkActionSlice.actions;

export const selectSyncStatus = (state) => state.bulkAction.status;
export const selectCurrentSyncId = (state) => state.bulkAction.currentSyncId;
export const selectSyncError = (state) => state.bulkAction.error;
export const selectIsProcessing = (state) => state.bulkAction.isProcessing;

export default bulkActionSlice.reducer;